import React, { useContext, useEffect, useState } from 'react';
import { sortBy, uniq } from 'lodash';
import { Plus } from 'lucide-react';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import { ArcaContext } from '../../../context/ArcaContext.context';
import { PageHeader } from '../../../components/ui/page-header';
import { Button } from '../../../components/ui/button';
import { CardSlider } from '../../../components/ui/card-slider';
import { Card, CardContent } from '../../../components/ui/card';
import { UserPlanI } from '../../../api/users/getPlans';
import getPlansValueHistory, { PlanValueHistoryI } from '../../../api/users/getPlansValueHistory';
import ResumenPlanCard from '../../../components/ResumenMulticuenta/ResumenPlanCard';
import FiltroPlanes from '../../../components/ResumenMulticuenta/FiltroPlanes';
import MetricasResumen from '../../../components/ResumenMulticuenta/MetricasResumen';
import BarraDelAnio from '../../../components/ResumenMulticuenta/BarraDelAnio';
import EvolucionPlanes from '../../../components/ResumenMulticuenta/EvolucionPlanes';
import UltimosMovimientos from '../../../components/ResumenMulticuenta/UltimosMovimientos';
import TarjetaAccionPendiente from '../../../components/Traspasos/TarjetaAccionPendiente';
import PensionPlanTransferJourneyV2 from '../../../components/PensionPlanTransferJourneyV2';
import OtherPlansSheet from '../../../components/MiBeneficioV2/OtherPlansSheet';
import { useToast } from '../../../components/ui/use-toast';
import { startKyc } from '../../../api/kyc';
import startTransferSignatureKyc from '../../../api/kyc/startTransferSignatureKyc';
import { PensionPlanTransfer } from '../../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { ARCA_PLANS } from '../../../constants/arcaPlans';
import useResumenMulticuenta from '../../../hooks/useResumenMulticuenta';
import useDatosDePlanes from '../../../hooks/useDatosDePlanes';
import { isLivePlan } from '../../../utils/viewOptions';
import {
	barraDelAnio,
	cifrasDelPeriodo,
	conDatos,
	resumenDelFiltro
} from '../../../utils/resumenMulticuenta/agregado';
import { ultimosMovimientos } from '../../../utils/resumenMulticuenta/movimientos';
import {
	DESDE_EL_INICIO,
	PeriodoEvolucion,
	aniosConDatos,
	partidaDelPlan
} from '../../../utils/resumenMulticuenta/evolucion';
import { anioDeMadrid } from '../../../utils/resumenMulticuenta/formato';
import { buildResumenPlans } from '../../../utils/resumenMulticuenta/planes';
import { completadoPorVer } from '../../../utils/resumenMulticuenta/planVisto';
import { selectPendingOperation } from '../../../utils/resumenMulticuenta/operacionPendiente';

export interface ResumenMulticuentaProps {
	/** `GET /users/plans`, ya cargado por el Dashboard. */
	plans: UserPlanI[];
	pendingPersonalPlan: boolean;
	pendingPlanArcaId: string | null | undefined;
	/** Abre un plan: el de empleo en su vista, el personal (o el que está en proceso) en la suya. */
	onVerPlan: (planId: string) => void;
	/** Para el progreso de los traspasos y el año de las aportaciones; inyectable en tests. */
	hoy?: Date;
	/** Salario bruto anual (`current-user`): pasa a euros una aportación de empleo en porcentaje. */
	salario?: number | null;
	/** €/mes de la empresa (`calculatedEmployerContribution`): solo del empleo de la empresa actual. */
	aportacionEmpresa?: number | null;
}

/** Cuántos movimientos se listan: los últimos, de todos los planes. */
const MOVIMIENTOS_A_LISTAR = 5;

/**
 * Resumen multicuenta (chirigota#585): la pantalla de aterrizaje de quien tiene 2 planes o más,
 * siguiendo `Screens/Resumen Multicuenta` de Proa. A la izquierda, la operación pendiente (una
 * sola), el filtro de planes, las cifras grandes, la barra del año y los últimos movimientos; a la
 * derecha, una PlanCard por plan con su rentabilidad, su aportación y su traspaso vivo. En móvil
 * las cards van debajo, en un carrusel (`ui/card-slider`).
 *
 * La tarjeta de operación pendiente se resuelve AQUÍ, con las mismas llamadas que Inicio: arrancar
 * el KYC, la hoja de elegir destino (`OtherPlansSheet`) o la firma del boletín. Llevar a la persona
 * a su plan no valía: Inicio solo enseña la acción de su traspaso principal, y en empleo no hay
 * dónde elegir destino, así que el botón podía acabar en una pantalla sin esa acción.
 *
 * La gráfica de evolución (Fase B, chirigota#613) pide su serie aparte, una vez: si falla, dice
 * «Sin dato» y el resto del Resumen sigue.
 *
 * Fuera: el rescate en curso dentro de la card.
 */
const ResumenMulticuenta: React.FC<ResumenMulticuentaProps> = ({
	plans,
	pendingPersonalPlan,
	pendingPlanArcaId,
	onVerPlan,
	hoy = new Date(),
	salario,
	aportacionEmpresa
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, language, companyId } = useContext(ArcaContext);
	const { toast } = useToast();
	const { kycStatus, kycResuelto, transfers, signedBulletinIds, recargar } =
		useResumenMulticuenta(token);
	/** El traspaso cuyo destino se está eligiendo (hoja abierta). */
	const [eligiendoDestino, setEligiendoDestino] = useState<PensionPlanTransfer | null>(null);
	/** Solo se pide elegir destino sobre un traspaso: la operación «destino» siempre lo trae. */
	const abrirDestino = (transfer: PensionPlanTransfer | undefined) =>
		setEligiendoDestino(transfer as PensionPlanTransfer);
	// «+ Añadir otro plan» abre el flujo de alta que ya existe (el de «Añadir traspaso» en Inicio).
	// El modal de 3 opciones (traspasar / registrar externo / abrir nuevo) es otra tarea.
	const [anadiendoPlan, setAnadiendoPlan] = useState(false);
	const [historial, setHistorial] = useState<{
		estado: 'cargando' | 'error' | 'listo';
		datos: PlanValueHistoryI[];
	}>({ estado: 'cargando', datos: [] });
	/** Sube con «Volver a cargar»: vuelve a pedir la serie. */
	const [intento, setIntento] = useState(0);
	const reintentar = () => {
		setHistorial({ estado: 'cargando', datos: [] });
		setIntento((n) => n + 1);
	};

	useEffect(() => {
		if (!token) return;
		// Sin guarda de desmontaje: con React 18 un `setState` tras desmontar no hace nada.
		getPlansValueHistory(token)
			.then((respuesta) => setHistorial({ estado: 'listo', datos: respuesta.data ?? [] }))
			.catch(() => setHistorial({ estado: 'error', datos: [] }));
	}, [token, intento]);

	const planes = buildResumenPlans({
		plans,
		pendingPersonalPlan,
		pendingPlanArcaId,
		kycStatus,
		kycResuelto,
		transfers,
		signedBulletinIds,
		// El completado, en su card hasta que se entra a su plan (lo apunta el Dashboard).
		completadoPorVer
	});
	// Lo que se pide por plan (Fase A2): rentabilidad, aportación y movimientos de cada uno.
	const datos = useDatosDePlanes(token, plans.filter(isLivePlan));
	const empresaDelPlan = Object.fromEntries(plans.map((plan) => [plan.id, plan.companyId]));
	const planesConDatos = planes.map((plan) =>
		conDatos(plan, datos[plan.id], {
			hoy,
			salario,
			// Lo de la empresa solo va en el empleo VIGENTE (el de la empresa actual): el de una
			// empresa anterior ya no recibe nada de la actual.
			empresa:
				plan.tipo === 'EMPLEO' &&
				plan.estado === 'activo' &&
				!!companyId &&
				empresaDelPlan[plan.id] === companyId
					? aportacionEmpresa
					: 0
		})
	);
	const filtrables = planesConDatos.filter((plan) => plan.estado !== 'enProceso');
	const nombreDe = (nombre: string | null) => nombre ?? translate('resumen.planCard.unnamedPlan');
	// `null` = todos: así un plan que llegue después también entra sin tocar el filtro.
	const [seleccion, setSeleccion] = useState<string[] | null>(null);
	const elegidos = seleccion ?? filtrables.map((plan) => plan.id);
	// El selector de año, junto al de planes (Proa, `FilterRow`): los dos afectan a todo lo que hay
	// debajo en esta columna, no a las cards (Marisa, 21/10/2026).
	const [periodo, setPeriodo] = useState<PeriodoEvolucion>(DESDE_EL_INICIO);
	const anioEnCurso = String(anioDeMadrid(hoy));
	const anios = sortBy(uniq([anioEnCurso, ...aniosConDatos(historial.datos)])).reverse();
	const planPorId = Object.fromEntries(plans.map((plan) => [plan.id, plan]));
	const puntosDe = (planId: string) =>
		historial.datos.find((h) => h.userEppConfigId === planId)?.points ?? [];
	const cifras = Object.fromEntries(
		filtrables.map((plan) => [
			plan.id,
			cifrasDelPeriodo(plan, {
				periodo,
				hoy,
				movimientos: datos[plan.id]?.movimientos ?? null,
				puntos: puntosDe(plan.id),
				creadoEn: planPorId[plan.id].createdAt
			})
		])
	);
	const resumen = resumenDelFiltro(planesConDatos, elegidos, cifras);
	const barra = barraDelAnio(planesConDatos, { seleccion: elegidos, cifras });
	const anioPasado = periodo !== DESDE_EL_INICIO && periodo < anioEnCurso ? periodo : undefined;
	const evolucion = filtrables.map((plan) => ({
		id: plan.id,
		nombre: nombreDe(plan.nombre),
		creadoEn: planPorId[plan.id].createdAt,
		// La partida de la card, para que el año en curso parta de lo mismo que ella.
		partidaDelAnioEnCurso: partidaDelPlan(planPorId[plan.id]),
		puntos: puntosDe(plan.id),
		movimientos: datos[plan.id]?.movimientos ?? null,
		// Mientras carga lo del plan, la card aún no tiene cifra: no manda en el último punto.
		rentabilidad: plan.cargandoDatos ? undefined : plan.rentabilidad,
		rentabilidadAnio: plan.cargandoDatos ? undefined : plan.rentabilidadAnio
	}));
	// La serie sola no basta: el % sale de los movimientos de cada plan. Hasta que llegan, la gráfica
	// sigue cargando, para no decir «No hay datos» justo antes de pintarlos.
	const cargandoPlanes = filtrables.some(
		(plan) => elegidos.includes(plan.id) && plan.cargandoDatos
	);
	const estadoGrafica = !token
		? 'error'
		: historial.estado === 'listo' && cargandoPlanes
			? 'cargando'
			: historial.estado;
	const movimientos = ultimosMovimientos(
		filtrables
			.filter((plan) => elegidos.includes(plan.id))
			.map((plan) => ({
				plan: { id: plan.id, nombre: nombreDe(plan.nombre) },
				movimientos: datos[plan.id]?.movimientos ?? []
			})),
		MOVIMIENTOS_A_LISTAR,
		periodo === DESDE_EL_INICIO ? undefined : periodo
	);

	const operacion = selectPendingOperation({
		plans,
		pendingPersonalPlan,
		kycStatus,
		kycResuelto,
		transfers,
		signedBulletinIds
	});

	/** Mismo aviso que Inicio cuando la verificación no arranca. */
	const avisarKyc = () =>
		toast({
			variant: 'destructive',
			title: translate('individual.process.kycStartError.title'),
			description: translate('individual.process.kycStartError.description')
		});

	/** Como Inicio: el KYC se abre en otra pestaña, y al volver se vuelve a preguntar. */
	const verificarIdentidad = async () => {
		try {
			const respuesta = await startKyc(token);
			if (respuesta.success && respuesta.data?.url) {
				window.open(respuesta.data.url, '_blank', 'noopener,noreferrer');
				recargar();
				return;
			}
		} catch (error) {
			console.error('Error starting KYC from Resumen:', error);
		}
		avisarKyc();
	};

	/** Como Inicio: el KYC acotado a firmar ESTE boletín, volviendo a esta pantalla. */
	const firmar = async (transferId: string) => {
		try {
			const returnUrl = `${window.location.origin}${window.location.pathname}`;
			const respuesta = await startTransferSignatureKyc(token, { transferId, returnUrl });
			const url = respuesta.data?.url;
			if (!url) throw new Error('sin url');
			window.location.assign(url);
		} catch (error) {
			console.error('Error starting bulletin signature from Resumen:', error);
			toast({
				variant: 'destructive',
				title: translate('individual.process.signError.title'),
				description: translate('individual.process.signError.description')
			});
		}
	};

	const anadirPlan = (
		<Button variant="secondary" className="w-full" onClick={() => setAnadiendoPlan(true)}>
			<Plus className="h-3.5 w-3.5" aria-hidden="true" />
			{translate('resumen.addPlan')}
		</Button>
	);

	return (
		<PageHeader title={translate('resumen.title')} subtitle={translate('resumen.subtitle')}>
			{/* Dos columnas desde `lg`, como Proa: el resumen a la izquierda y las cards a la derecha. */}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(280px,1fr)]">
				<div className="min-w-0 space-y-4 lg:space-y-5" data-testid="resumen-agregado">
					{operacion && (
						<TarjetaAccionPendiente
							accion={operacion.accion}
							onVerificarIdentidad={verificarIdentidad}
							onElegirDestino={() => abrirDestino(operacion.transfer)}
							onFirmar={firmar}
						/>
					)}
					<FiltroPlanes
						planes={filtrables.map((plan) => ({ id: plan.id, nombre: nombreDe(plan.nombre) }))}
						seleccion={elegidos}
						onCambiar={setSeleccion}
						periodo={periodo}
						anios={anios}
						onPeriodo={setPeriodo}
						translate={translate}
					/>
					<MetricasResumen
						resumen={resumen}
						periodo={periodo}
						anioEnCurso={anioEnCurso}
						language={language}
						translate={translate}
					/>
					<Card className="border-none shadow-xs dark:border" data-testid="resumen-evolucion">
						<CardContent className="p-4 sm:p-6">
							<EvolucionPlanes
								planes={evolucion}
								elegidos={elegidos}
								periodo={periodo}
								// Sin token no se pide: el aviso de error, sin botón (nunca «Cargando…» para siempre).
								estado={estadoGrafica}
								onReintentar={token ? reintentar : undefined}
								hoy={hoy}
								language={language}
								translate={translate}
							/>
						</CardContent>
					</Card>
					<Card className="border-none shadow-xs dark:border">
						<CardContent className="p-4 sm:p-6">
							<BarraDelAnio
								barra={barra}
								anio={anioPasado}
								nombreDe={nombreDe}
								translate={translate}
							/>
						</CardContent>
					</Card>
					<Card className="border-none shadow-xs dark:border">
						<CardContent className="p-4 sm:p-6">
							<UltimosMovimientos
								movimientos={movimientos}
								anio={periodo === DESDE_EL_INICIO ? undefined : periodo}
								language={language}
								translate={translate}
							/>
						</CardContent>
					</Card>
				</div>

				<div className="min-w-0">
					{/* Escritorio: columna simple. Móvil y tablet (mismo corte `lg` que la navegación):
				    carrusel, para que N cards no compitan por el scroll con el resto de la pantalla. */}
					<div className="hidden space-y-3 lg:block" data-testid="resumen-planes-escritorio">
						{planesConDatos.map((plan) => (
							<ResumenPlanCard
								key={plan.id}
								plan={plan}
								onVerPlan={onVerPlan}
								hoy={hoy}
								language={language}
								condensed={false}
							/>
						))}
						{anadirPlan}
					</div>
					<div className="space-y-3 lg:hidden" data-testid="resumen-planes-movil">
						<CardSlider
							ariaLabel={translate('resumen.plansLabel')}
							items={planesConDatos.map((plan) => ({
								id: plan.id,
								label: nombreDe(plan.nombre),
								content: (
									<ResumenPlanCard
										plan={plan}
										onVerPlan={onVerPlan}
										hoy={hoy}
										language={language}
										condensed
									/>
								)
							}))}
						/>
						{anadirPlan}
					</div>
				</div>
			</div>
			<PensionPlanTransferJourneyV2
				isOpen={anadiendoPlan}
				onClose={() => {
					setAnadiendoPlan(false);
					recargar();
				}}
			/>
			{eligiendoDestino && (
				<OtherPlansSheet
					open
					transfer={eligiendoDestino}
					token={token}
					plans={ARCA_PLANS}
					translate={translate}
					onClose={() => setEligiendoDestino(null)}
					onPlanChanged={() => {
						// La hoja ya ha guardado el destino: se cierra y se recarga, y la tarjeta pasa sola
						// de «elegir destino» a «firmar».
						setEligiendoDestino(null);
						recargar();
					}}
				/>
			)}
		</PageHeader>
	);
};

export default ResumenMulticuenta;
